import { MAP_SPANS, PORTAL_RULES, horizontalDistance, isPortalWithinSearchRange, worldPointToMap } from './calculator';
import type { Dimension, Edition, ExistingPortal, HorizontalPoint } from './types';

interface RenderMapOptions {
  canvas: HTMLCanvasElement;
  dimension: Dimension;
  edition: Edition;
  center: HorizontalPoint;
  existingPortal: ExistingPortal | null;
}

function crispLine(context: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
  context.beginPath();
  context.moveTo(x1, y1);
  context.lineTo(x2, y2);
  context.stroke();
}

export function resizeCanvasForDisplay(canvas: HTMLCanvasElement): { width: number; height: number; dpr: number } {
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(280, Math.round(rect.width || 360));
  const height = Math.max(280, Math.round(rect.height || width));
  const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
  const backingWidth = Math.round(width * dpr);
  const backingHeight = Math.round(height * dpr);
  if (canvas.width !== backingWidth || canvas.height !== backingHeight) {
    canvas.width = backingWidth;
    canvas.height = backingHeight;
  }
  return { width, height, dpr };
}

export function renderPortalMap({ canvas, dimension, edition, center, existingPortal }: RenderMapOptions): void {
  const { width, height, dpr } = resizeCanvasForDisplay(canvas);
  const context = canvas.getContext('2d');
  if (!context) return;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, width, height);

  const isNether = dimension === 'nether';
  const background = context.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, isNether ? '#170a0c' : '#071813');
  background.addColorStop(1, isNether ? '#0d0507' : '#03100d');
  context.fillStyle = background;
  context.fillRect(0, 0, width, height);

  const span = MAP_SPANS[dimension];
  const pixelsPerBlock = width / span;
  const minorStep = dimension === 'overworld' ? 128 : 16;
  const majorEvery = 4;
  const startX = Math.floor((center.x - span / 2) / minorStep) * minorStep;
  const startZ = Math.floor((center.z - span / 2) / minorStep) * minorStep;
  context.lineWidth = 1;
  for (let x = startX, index = 0; x <= center.x + span / 2 + minorStep; x += minorStep, index += 1) {
    const px = width / 2 + (x - center.x) * pixelsPerBlock;
    context.strokeStyle = index % majorEvery === 0 ? 'rgba(121, 230, 73, .18)' : 'rgba(179, 213, 194, .07)';
    crispLine(context, Math.round(px) + 0.5, 0, Math.round(px) + 0.5, height);
  }
  for (let z = startZ, index = 0; z <= center.z + span / 2 + minorStep; z += minorStep, index += 1) {
    const py = height / 2 + (z - center.z) * pixelsPerBlock;
    context.strokeStyle = index % majorEvery === 0 ? 'rgba(121, 230, 73, .18)' : 'rgba(179, 213, 194, .07)';
    crispLine(context, 0, Math.round(py) + 0.5, width, Math.round(py) + 0.5);
  }

  const radiusBlocks = dimension === 'overworld'
    ? PORTAL_RULES[edition].overworldSearchRadius
    : PORTAL_RULES[edition].netherSearchRadius;
  const radiusPixels = radiusBlocks * pixelsPerBlock;
  context.fillStyle = isNether ? 'rgba(255, 91, 102, .12)' : 'rgba(138, 232, 63, .11)';
  context.fillRect(width / 2 - radiusPixels, height / 2 - radiusPixels, radiusPixels * 2, radiusPixels * 2);
  context.lineWidth = 2;
  context.setLineDash([7, 5]);
  context.strokeStyle = isNether ? '#ff6f78' : '#8ae83f';
  context.strokeRect(width / 2 - radiusPixels, height / 2 - radiusPixels, radiusPixels * 2, radiusPixels * 2);
  context.setLineDash([]);

  if (edition === 'bedrock' && dimension === 'overworld') {
    const projectedRadius = PORTAL_RULES.bedrock.netherSearchRadius * 8 * pixelsPerBlock;
    context.lineWidth = 1.5;
    context.setLineDash([3, 5]);
    context.strokeStyle = 'rgba(189, 124, 255, .7)';
    context.strokeRect(width / 2 - projectedRadius, height / 2 - projectedRadius, projectedRadius * 2, projectedRadius * 2);
    context.setLineDash([]);
  }

  context.fillStyle = '#ffffff';
  context.beginPath();
  context.arc(width / 2, height / 2, 5, 0, Math.PI * 2);
  context.fill();
  context.strokeStyle = '#020908';
  context.lineWidth = 2;
  context.stroke();
  context.fillStyle = 'rgba(255,255,255,.84)';
  context.font = '700 11px system-ui, sans-serif';
  context.fillText('YOU', width / 2 + 10, height / 2 - 9);

  if (existingPortal) {
    const point = existingPortal[dimension];
    const mapped = worldPointToMap(point, center, span, width, height);
    if (mapped.x >= -12 && mapped.x <= width + 12 && mapped.y >= -12 && mapped.y <= height + 12) {
      context.save();
      context.translate(mapped.x, mapped.y);
      context.rotate(Math.PI / 4);
      context.fillStyle = '#aa65ff';
      context.fillRect(-7, -7, 14, 14);
      context.strokeStyle = '#f4e8ff';
      context.lineWidth = 2;
      context.strokeRect(-7, -7, 14, 14);
      context.restore();
      context.fillStyle = '#ead8ff';
      context.font = '700 11px system-ui, sans-serif';
      context.fillText('PORTAL', Math.min(width - 50, mapped.x + 12), Math.max(14, mapped.y - 10));
    }
  }

  context.strokeStyle = 'rgba(210, 236, 222, .28)';
  context.lineWidth = 1;
  context.strokeRect(0.5, 0.5, width - 1, height - 1);
}

export function describePortalStatus(
  dimension: Dimension,
  edition: Edition,
  center: HorizontalPoint,
  portal: ExistingPortal | null,
): string {
  const radius = dimension === 'overworld'
    ? PORTAL_RULES[edition].overworldSearchRadius
    : PORTAL_RULES[edition].netherSearchRadius;
  if (!portal) return `Search radius: ${radius} blocks. No existing portal placed.`;
  const point = portal[dimension];
  const distance = horizontalDistance(center, point);
  const inside = isPortalWithinSearchRange(center, point, radius);
  return `Existing portal X ${point.x}, Z ${point.z} is ${distance.toFixed(1)} horizontal blocks from the target and ${inside ? 'inside' : 'outside'} the inclusive ±${radius} X/Z search area.`;
}
